// ---------------------------------------------------------------------------
// Calendar — ONE project, on that project's key date.
//   · drag a task chip onto another day  → reschedules it
//   · click a day's ＋                    → create a task dated that day
//   · chips tint by urgency, dim when done, ⛔ when blocked
//
// The grid itself is MonthGrid.tsx, shared with My calendar. What stays here is
// everything project-specific: which tasks, which date field, the filter bar,
// and the loading/error states.
// ---------------------------------------------------------------------------

import { useCallback, useEffect } from 'react';
import type { TaskListItem } from '@cg-projects/shared';
import { useTasksStore } from '@/features/tasks/store/tasksStore';
import { useAreaStore } from '@/features/areas/store/areaStore';
import { useFilterStore } from '@/features/filters/store/filterStore';
import { filterTasks } from '@/features/tasks/filter';
import { patchTask, createTask } from '@/features/tasks/mutations';
import { Spinner, LoadError } from '@/core/ui/atoms';
import { MonthGrid } from './MonthGrid';

export function CalendarView({
  areaId,
  dateField,
}: {
  areaId: number;
  dateField: 'film_date' | 'date_deadline';
}) {
  const tasks = useTasksStore((s) => s.tasksByArea[areaId]);
  const loading = useTasksStore((s) => s.loadingArea[areaId]);
  // Without this a failed load rendered an empty month grid — indistinguishable
  // from "nothing is scheduled".
  const error = useTasksStore((s) => s.errorByArea[areaId]);
  const loadTasks = useTasksStore((s) => s.loadTasks);
  const stages = useAreaStore((s) => s.stagesByArea[areaId]);
  const loadStages = useAreaStore((s) => s.loadStages);
  const search = useFilterStore((s) => s.search);
  const assigneeId = useFilterStore((s) => s.assigneeId);
  const onlyUnassigned = useFilterStore((s) => s.onlyUnassigned);
  const dueThisWeek = useFilterStore((s) => s.dueThisWeek);
  const tagId = useFilterStore((s) => s.tagId);
  const stageId = useFilterStore((s) => s.stageId);
  const showCompleted = useFilterStore((s) => s.showCompleted);

  useEffect(() => { void loadTasks(areaId, showCompleted); }, [areaId, showCompleted, loadTasks]);
  useEffect(() => { void loadStages(areaId); }, [areaId, loadStages]);

  const visible = filterTasks(tasks ?? [], { search, assigneeId, onlyUnassigned, dueThisWeek, tagId, stageId, dateField });

  // Fall back to the other date so deadline-only tasks in content teams (and
  // film-date-only tasks elsewhere) don't vanish from the calendar.
  const dateOf = useCallback(
    (t: TaskListItem) => (dateField === 'film_date' ? (t.filmDate ?? t.deadline) : (t.deadline ?? t.filmDate)),
    [dateField],
  );

  // Rescheduling writes the area's OWN key date, never whichever one the fallback
  // happened to display — dragging a card must not silently move the other field.
  const onReschedule = useCallback(
    (t: TaskListItem, dateKey: string) => {
      if (dateField === 'film_date') void patchTask(t.id, { filmDate: dateKey }, { filmDate: dateKey });
      else void patchTask(t.id, { deadline: dateKey }, { deadline: dateKey });
    },
    [dateField],
  );

  const defaultStageId = stages?.[0]?.id;
  const onCreate = useCallback(
    async (dateKey: string, name: string) => {
      const dates = dateField === 'film_date' ? { filmDate: dateKey } : { deadline: dateKey };
      return Boolean(await createTask({ areaId, name, stageId: defaultStageId, ...dates }));
    },
    [areaId, dateField, defaultStageId],
  );

  if (error) {
    return (
      <div className="flex h-full flex-1 items-center justify-center">
        <LoadError message={error} onRetry={() => void loadTasks(areaId, showCompleted, true)} />
      </div>
    );
  }
  if (loading && !tasks) {
    return <div className="flex h-full flex-1 items-center justify-center"><Spinner label="Loading…" /></div>;
  }

  return <MonthGrid tasks={visible} dateOf={dateOf} onReschedule={onReschedule} onCreate={onCreate} />;
}
